import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconButton, Drawer, Stack } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { EXTERNAL_LINKS } from "../../../../constants/externalLinks";
import { ROUTES } from "../../../../constants/routes";
import { useState } from "react";
import { Button } from "../../../../ui/Button/Button";
import classes from "./styles.module.scss";
import { ReactComponent as YnaLogoRect } from "../../../../assets/icons/yna_logo_rect.svg";

export const Header = observer(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isRegisterPage = location.pathname === ROUTES.LOGIN + ROUTES.REGISTER;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToAuthForm = () => {
    const formElement = document.getElementById("auth-form");
    if (!formElement) return;

    let scrollContainer: HTMLElement | null = formElement.parentElement;
    while (scrollContainer) {
      const hasScrollableContent =
        scrollContainer.scrollHeight > scrollContainer.clientHeight;
      const overflowY = window.getComputedStyle(scrollContainer).overflowY;
      const isScrollable =
        hasScrollableContent &&
        (overflowY === "auto" || overflowY === "scroll");

      if (isScrollable) break;
      scrollContainer = scrollContainer.parentElement;
    }

    if (scrollContainer) {
      const targetTop = formElement.offsetTop;
      scrollContainer.scrollTo({ top: targetTop, behavior: "smooth" });
    } else {
      formElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className={classes.header}>
        <div className={classes.left}>
          <YnaLogoRect
            className={classes.logo}
            onClick={() => {
              window.location.href = EXTERNAL_LINKS.YNA_HOME;
            }}
          />
        </div>
        <div className={classes.right}>
          <div className={classes.desktopMenu}>
            <a
              href={EXTERNAL_LINKS.YNA_ABOUT}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              O YNA
            </a>
            <a
              href={EXTERNAL_LINKS.YNA_SUPPORT}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              Поддержка
            </a>
            {isRegisterPage ? (
              <Button
                buttonType="primary"
                onClick={() => navigate(ROUTES.LOGIN)}
                className={classes.button}
              >
                {t("login")}
              </Button>
            ) : (
              <Button
                buttonType="primary"
                onClick={() => navigate(ROUTES.LOGIN + ROUTES.REGISTER)}
                className={classes.button}
              >
                {t("join")}
              </Button>
            )}
          </div>
          <IconButton
            className={classes.burgerButton}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="menu"
          >
            <MenuIcon />
          </IconButton>
        </div>
      </header>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        classes={{ paper: classes.menu }}
      >
        <Stack alignItems="flex-end" gap={24} className={classes.menuContent}>
          <IconButton
            className={classes.menuCloseButton}
            onClick={() => setMobileMenuOpen(false)}
            aria-label="close menu"
          >
            <CloseIcon />
          </IconButton>
          <Stack
            direction="column"
            alignItems="flex-end"
            className={classes.menuLinks}
          >
            <a
              href={EXTERNAL_LINKS.YNA_ABOUT}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.menuLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              O YNA
            </a>
            <a
              href={EXTERNAL_LINKS.YNA_SUPPORT}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.menuLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Поддержка
            </a>
          </Stack>
          <Stack gap={10} className={classes.menuButtons}>
            <div className={classes.menuDivider} />
            <Button
              buttonType="secondary"
              onClick={() => handleNavigate(ROUTES.LOGIN)}
              className={classes.menuButtonSecondary}
            >
              {t("login")}
            </Button>
            <Button
              buttonType="primary"
              onClick={() => {
                if (isRegisterPage) {
                  scrollToAuthForm();
                } else {
                  navigate(ROUTES.LOGIN + ROUTES.REGISTER);
                  setTimeout(scrollToAuthForm, 300);
                }
                setMobileMenuOpen(false);
              }}
              className={classes.menuButton}
            >
              {t("join")}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
});
